import { ErrorPage, NotificationActions } from '@elotech/components';
import { AxiosRequestConfig } from 'axios';
import { Component, ReactNode } from 'react';
import { connect } from 'react-redux';

export const SILENT_REJECTION = 'SILENT_REJECTION';
export const ignoreRequestError = (config: AxiosRequestConfig = {}) => {
  return { ...config, ignoreError: true } as AxiosRequestConfig;
};

type Props = {
  children: ReactNode;
  showNotification: (action: any) => void;
};

type State = {
  hasError: boolean;
  error: any;
  info: any;
};

const DEFAULT_MESSAGE = 'Ops, ocorreu algum problema inesperado.';
const DEFAULT_VMESSAGE_VIOLATION = 'Validação.';

export const getErrorMessageDetails = async (event: any) => {
  const error = event.reason || event.error || event;

  // Não mostra mensagem se for cancelamento por parte do usuário
  if (error === SILENT_REJECTION || error?.__proto__?.__CANCEL__) {
    return;
  }

  const reason = error?.response || error || event;

  const violations = error?.response?.data
    ? (await getErrorData(error.response))?.violations
    : null;
  if (violations?.length) {
    return {
      level: 'error',
      title: await getErrorMessage(reason, true),
      message: violations.map(violation => violation.message).join(' / ')
    };
  }

  return {
    level: 'error',
    message: await getErrorMessage(reason, false)
  };
};

const exceptionFields = [
  'timestamp',
  'status',
  'error',
  'exception',
  'message',
  'path',
  'detail'
] as const;

const tryParse = (data: string) => {
  if (!data) {
    return data;
  }
  try {
    return JSON.parse(data);
  } catch (e) {
    try {
      const regex = new RegExp(
        `"(${exceptionFields.join(
          '|'
        )})"\\s*:\\s*("([^"]*?)"|([^,}\\n]*?))\\s*(?=,|\\})`,
        'gm'
      );
      const matches = data.matchAll(regex);
      const collectedInfo = {} as Record<string, string>;
      for (const match of matches) {
        collectedInfo[match[1]] = match[2].replace(/^undefined$/, 'null');
      }

      for (const key in collectedInfo) {
        collectedInfo[key] = JSON.parse(collectedInfo[key]);
      }

      for (const field of exceptionFields) {
        if (!(field in collectedInfo)) {
          return data;
        }
      }

      return collectedInfo;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug('Erro ao tentar analisar a resposta de erro', data, e);
    }
    return data;
  }
};

const getErrorData = async ({ data }) => {
  const parsableMimeTypes = ['text/plain', 'application/json'] as const;

  const parsableBlob =
    data?.constructor?.name === 'Blob' && parsableMimeTypes.includes(data.type);

  const processingText = parsableBlob ? await data.text() : data;

  const shouldTryParse = parsableBlob || data?.constructor?.name === 'String';

  if (shouldTryParse) {
    return tryParse(processingText);
  }
  return data;
};

const getErrorMessage = async (error: any, violationError: boolean) => {
  if (!error?.data) {
    return error?.message || DEFAULT_MESSAGE;
  }
  const data = await getErrorData(error);
  // eslint-disable-next-line no-console
  console.warn('Erro recebido do servidor', data);
  if (data && data.message) {
    return violationError ? DEFAULT_VMESSAGE_VIOLATION : data.message;
  }
  return typeof data === 'object' ? DEFAULT_MESSAGE : data;
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      info: null
    };
  }

  componentDidMount() {
    window.addEventListener('unhandledrejection', this.handleRejections);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleRejections);
  }

  componentDidCatch(error, info) {
    this.setState({
      hasError: true,
      error,
      info
    });
  }

  handleRejections = async event => {
    //eslint-disable-next-line no-console
    console.warn(
      'Rejeição de promise não tratada',
      { event },
      event?.error || event?.reason || event
    );
    event.preventDefault?.();
    event.stopPropagation?.();
    if (event?.error?.config?.ignoreError) {
      return false;
    }
    getErrorMessageDetails(event).then(
      details => details && this.props.showNotification(details)
    );
    return false;
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorPage message={this.state.error?.toString() || DEFAULT_MESSAGE} />
      );
    }

    return this.props.children;
  }
}

const mapDispatchToProps = {
  showNotification: NotificationActions.showNotification
};

const ConnectedErrorBoundary = connect(null, mapDispatchToProps)(ErrorBoundary);

export { ConnectedErrorBoundary as default, ErrorBoundary };
